import Quill from 'quill/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useThrottledCallback } from 'use-debounce';
import useAppSelector from '../../app/redux/useAppSelector';
import Header from '../../editor/formats/Header';
import { useQuill } from '../../editor/QuillContext';
import listenToPostScroll from '../listenToPostScroll';

export type TOCHeader = {
  id: string;
  text: string;
  level: number;
};

export type TOCState = {
  activeH1: string | null;
  activeH2: string | null;
  activeH3: string | null;
  headers: TOCHeader[];
};

interface ConnectedTableOfContentsProps {
  children: (state: TOCState) => React.ReactNode;
  postId: string;
}

const ConnectedTableOfContents = ({
  children,
  postId,
}: ConnectedTableOfContentsProps) => {
  const version = useAppSelector((state) => state.editor.version);
  const { quill } = useQuill();
  const [state, setState] = useState<TOCState>({
    activeH1: null,
    activeH2: null,
    activeH3: null,
    headers: [],
  });

  const { callback: onScroll } = useThrottledCallback(
    ({ offsetTop }: { offsetTop: number }) => {
      let activeH1: string | null = null;
      let activeH2: string | null = null;
      let activeH3: string | null = null;

      state.headers.some(({ id }) => {
        const node = document.getElementById(id);
        if (!node) return false;
        if (node.getBoundingClientRect().top - offsetTop > 0) {
          return true;
        }
        if (node.tagName === 'H1') {
          activeH1 = id;
          activeH2 = null;
          activeH3 = null;
        } else if (node.tagName === 'H2') {
          activeH2 = id;
          activeH3 = null;
        } else if (node.tagName === 'H3') {
          activeH3 = id;
        }
        return false;
      });

      setState((s) => ({ ...s, activeH1, activeH2, activeH3 }));
    },
    100,
  );

  const generateHeaders = useCallback(() => {
    const headers: TOCHeader[] = [];

    if (quill) {
      // @ts-expect-error
      (quill.scroll.descendants(Header) as Header[]).forEach((header) => {
        const id = header.getId();
        if (id.length > 0) {
          headers.push({
            id: header.getId(),
            text: header.getText(),
            level: header.statics.formats(header.domNode),
          });
        }
      });

      setState((s) => ({ ...s, headers }));
    }
  }, [quill, setState]);

  const throttledGenerateHeaders = useThrottledCallback(generateHeaders, 2000);

  useEffect(() => {
    const removeScrollListener = listenToPostScroll(onScroll);
    return () => {
      removeScrollListener();
      throttledGenerateHeaders.cancel();
    };
  }, [onScroll, throttledGenerateHeaders]);

  useEffect(() => {
    if (quill) {
      // new post loaded
      generateHeaders();

      quill.once(Quill.events.TEXT_CHANGE, () => generateHeaders());
    }
  }, [quill, postId, generateHeaders]);

  useEffect(() => {
    if (quill) {
      throttledGenerateHeaders.callback();
    }
  }, [quill, version, throttledGenerateHeaders]);

  return <>{children(state)}</>;
};

export default ConnectedTableOfContents;
